import moment from 'moment-timezone';
import { z } from 'zod/v4';
import { getRKIRegistrations } from '../customers/helpers.js';
import { OrganizationIdentifierScheme } from '../customers/types.js';
import { DebtRegistrationState, RegistryType, } from '../debt-register/index.js';
import economyHelpers from '../economy/helpers/index.js';
import { VoucherCategory } from '../economy/vouchers/index.js';
import { Entity, validateAddress, validateCPR, validateCVR, validateEmail, validatePhone, } from '../index.js';
import { EBoksRecipientType } from '../interactions/eboks.js';
import { Direction, InteractionType, isNameAndAddressAttemptAt, } from '../interactions/index.js';
import { PropertyDataType, } from '../properties/index.js';
import { UpdateState } from '../updates/index.js';
export var TodoType;
(function (TodoType) {
    TodoType["AddVoucher"] = "ADD_VOUCHER";
    TodoType["CreateInteraction"] = "CREATE_INTERACTION";
    TodoType["CreateCreditorInteraction"] = "CREATE_CREDITOR_INTERACTION";
    TodoType["CreateOtherInteraction"] = "CREATE_OTHER_INTERACTION";
    TodoType["SetCaseGroup"] = "SET_CASE_GROUP";
    TodoType["ChangeCaseStatus"] = "CHANGE_CASE_STATUS";
    TodoType["SetCaseTag"] = "SET_ECONOMY_ACCOUNT_TAG";
    TodoType["RemoveCaseTag"] = "REMOVE_CASE_TAG";
    TodoType["AddBillingVoucher"] = "ADD_BILLING_VOUCHER";
    TodoType["OutboundCall"] = "OUTBOUND_CALL";
    TodoType["SetCollector"] = "SET_COLLECTOR";
    TodoType["RemoveVoucher"] = "REMOVE_VOUCHER";
    TodoType["MergeCase"] = "MERGE_CASE";
    TodoType["CreateProperties"] = "CREATE_PROPERTIES";
    TodoType["SetResponsible"] = "SET_RESPONSIBLE";
    TodoType["SetCaseSetting"] = "SET_CASE_SETTING";
    TodoType["ConnectToDebtCollector"] = "CONNECT_TO_DEBT_COLLECTOR";
    TodoType["ReturnToConnectSource"] = "RETURN_TO_CONNECT_SOURCE";
    TodoType["MoveToTenant"] = "MOVE_TO_TENANT";
    TodoType["DebtRegister"] = "DEBT_REGISTER";
    TodoType["DebtRegisterSkip"] = "DEBT_REGISTER_SKIP";
    TodoType["SubmitToCourt"] = "SUBMIT_TO_COURT";
    TodoType["ReadyToSubmitToCourt"] = "READY_TO_SUBMIT_TO_COURT";
    TodoType["SwitchWorkflow"] = "SWITCH_WORKFLOW";
    TodoType["SwitchBackToPreviousWorkflow"] = "SWITCH_BACK_TO_PREVIOUS_WORKFLOW";
    TodoType["MarkExpired"] = "MARK_EXPIRED";
    TodoType["Bespoke"] = "BESPOKE";
    TodoType["TriggerAgent"] = "TRIGGER_AGENT";
})(TodoType || (TodoType = {}));
export const TRIGGER_AGENT_PROMPT_MAX_LENGTH = 8000;
export var TimingType;
(function (TimingType) {
    TimingType["Timespan"] = "TIMESPAN";
    TimingType["TimespanFromDueDate"] = "TIMESPAN_FROM_DUE_DATE";
})(TimingType || (TimingType = {}));
export var RequirementType;
(function (RequirementType) {
    RequirementType["HasValidEmail"] = "HAS_VALID_EMAIL";
    RequirementType["HasNoValidEmail"] = "HAS_NO_VALID_EMAIL";
    RequirementType["HasValidAddress"] = "HAS_VALID_ADDRESS";
    RequirementType["HasNoValidAddress"] = "HAS_NO_VALID_ADDRESS";
    RequirementType["HasValidPhone"] = "HAS_VALID_PHONE";
    RequirementType["HasNoValidPhone"] = "HAS_NO_VALID_PHONE";
    RequirementType["HasValidCentralRegisterId"] = "HAS_VALID_CENTRAL_REGISTER_ID";
    RequirementType["HasNoValidCentralRegisterId"] = "HAS_NO_VALID_CENTRAL_REGISTER_ID";
    RequirementType["IsPrivate"] = "IS_PRIVATE";
    RequirementType["IsCompany"] = "IS_COMPANY";
    RequirementType["DebtRemarks"] = "DEBT_REMARKS";
    RequirementType["HasIncomingInteractions"] = "HAS_INCOMING_INTERACTIONS";
    RequirementType["ReadyForEInvoice"] = "READY_FOR_EINVOICE";
    RequirementType["ReadyForEboks"] = "READY_FOR_EBOKS";
    RequirementType["HasUpdate"] = "HAS_UPDATE";
    RequirementType["MissingUpdate"] = "DOES_NOT_HAVE_UPDATE";
    RequirementType["AmountThreshold"] = "AMOUNT_THRESHOLD";
    RequirementType["PercentageSplit"] = "PERCENTAGE_SPLIT";
    RequirementType["CaseGroup"] = "CASE_GROUP";
    RequirementType["HasFoundation"] = "HAS_FOUNDATION";
    RequirementType["DoesNotHaveFoundation"] = "DOES_NOT_HAVE_FOUNDATION";
    RequirementType["SpecificCreditor"] = "SPECIFIC_CREDITOR";
    RequirementType["CustomerHasTag"] = "CUSTOMER_HAS_TAG";
    RequirementType["CaseHasTag"] = "CASE_HAS_TAG";
    RequirementType["CaseStatusIn"] = "CASE_STATUS_IN";
    RequirementType["Property"] = "PROPERTY";
    RequirementType["InDebtRegister"] = "IN_DEBT_REGISTER";
    RequirementType["NotInDebtRegister"] = "NOT_IN_DEBT_REGISTER";
})(RequirementType || (RequirementType = {}));
export const ishHandledPropertyType = (propertyType) => [
    PropertyDataType.singleLineText,
    PropertyDataType.dropdown,
    PropertyDataType.checkbox,
].includes(propertyType.type) &&
    [Entity.Case, Entity.CaseVoucher, Entity.Customer, Entity.Creditor].includes(propertyType.ref);
export var PathType;
(function (PathType) {
    PathType["Normal"] = "NORMAL";
    PathType["ForMigration"] = "FOR_MIGRATION";
    PathType["DeprecatedTask"] = "DEPRECATED_TASK";
})(PathType || (PathType = {}));
export const CreateWorkflowSchema = z.object({
    title: z.string(),
    description: z.string(),
    tasks: z.array(z.any()),
    paths: z.array(z.any()),
    clonedFromWorkflowId: z.uuid().optional(),
    settings: z.any().optional(),
});
export const UpdateWorkflowSchema = z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    tasks: z.any().optional(),
    paths: z.any().optional(),
    settings: z.any().optional(),
});
export const addTiming = (from, timing, caseVouchers) => {
    switch (timing.type) {
        case TimingType.Timespan: {
            const time = moment(from).add(timing.details.timespan[0], timing.details.timespan[1]);
            if (timing.details.time) {
                time
                    .tz('Europe/Copenhagen')
                    .set('hour', timing.details.time.hour)
                    .set('minute', timing.details.time.minute)
                    .startOf('minute');
            }
            return time.tz('UTC').format();
        }
        case TimingType.TimespanFromDueDate: {
            if (!caseVouchers) {
                throw new Error('No case vouchers provided');
            }
            const vouchersSorted = caseVouchers
                .filter((caseVoucher) => caseVoucher.category === VoucherCategory.PRINCIPAL &&
                caseVoucher.dueDate)
                .sort((a, b) => new Date(b.dueDate || b.date).getTime() -
                new Date(a.dueDate || a.date).getTime());
            const latestDueDate = (vouchersSorted[0] && vouchersSorted[0].dueDate) || undefined;
            const timeFromDueDate = moment(latestDueDate || from).add(timing.details.timespan[0], timing.details.timespan[1]);
            if (timing.details.time) {
                timeFromDueDate
                    .tz('Europe/Copenhagen')
                    .set('hour', timing.details.time.hour)
                    .set('minute', timing.details.time.minute)
                    .startOf('minute');
            }
            return timeFromDueDate.tz('UTC').format();
        }
        default:
            throw new Error('TimingType not handled');
    }
};
const uuidToFraction = (uuid) => {
    return parseInt(uuid.slice(0, 8), 16) / 16 ** 8;
};
const resonsForNotDeliverable = [
    'mailbox_does_not_exist',
    // 'smtp_timeout',
    // 'smtp_error',
    'No MX records found for domain',
    'failed custom grammar check for',
];
export const fulfilRequirements = (requirements, context) => {
    const centralRegisterIdIsValid = context.contact
        ? context.isCompany
            ? validateCVR(context.contact.cvr)
            : validateCPR(context.contact.cpr)
        : null;
    return requirements.every((requirement) => {
        switch (requirement.type) {
            case RequirementType.HasValidEmail: {
                const emailSyntaxIsValid = validateEmail(context.contact?.email || '');
                const emailValidation = context.contact &&
                    context.contact.emailValidations &&
                    context.contact.emailValidations.find((emailValidation) => emailValidation.address === context.contact?.email);
                const emailExist = !(emailValidation && emailValidation.reason
                    ? emailValidation.reason.some((reason) => resonsForNotDeliverable.some((_reason) => reason.indexOf(_reason) > -1))
                    : false);
                return emailSyntaxIsValid && emailExist;
            }
            case RequirementType.HasNoValidEmail: {
                const noEmailSyntaxIsValid = validateEmail(context.contact?.email || '');
                const noEmailValidation = context.contact &&
                    context.contact.emailValidations &&
                    context.contact.emailValidations.find((emailValidation) => emailValidation.address === context.contact?.email);
                const noEmailExist = !(noEmailValidation &&
                    noEmailValidation.reason
                    ? noEmailValidation.reason.some((reason) => resonsForNotDeliverable.some((_reason) => reason.indexOf(_reason) > -1))
                    : false);
                return !(noEmailSyntaxIsValid && noEmailExist);
            }
            case RequirementType.HasValidPhone: {
                const phoneSyntaxValid = validatePhone(context.contact?.phone);
                const phoneValidation = context.contact?.phoneValidations?.find((phoneValidation) => phoneValidation.phone === context.contact?.phone);
                const phoneExist = !(phoneValidation && phoneValidation.validNumber == 'not_valid');
                return phoneSyntaxValid && phoneExist;
            }
            case RequirementType.HasNoValidPhone: {
                const phoneSyntaxValid = validatePhone(context.contact?.phone);
                const phoneValidation = context.contact?.phoneValidations?.find((phoneValidation) => phoneValidation.phone === context.contact?.phone);
                const phoneExist = !(phoneValidation && phoneValidation.validNumber == 'not_valid');
                return !(phoneSyntaxValid && phoneExist);
            }
            case RequirementType.HasValidAddress:
                return addressValid(context.contact);
            case RequirementType.HasNoValidAddress:
                return !addressValid(context.contact);
            case RequirementType.HasValidCentralRegisterId:
                return centralRegisterIdIsValid ?? false;
            case RequirementType.HasNoValidCentralRegisterId:
                return !centralRegisterIdIsValid;
            case RequirementType.IsCompany:
                return context.isCompany;
            case RequirementType.IsPrivate:
                return !context.isCompany;
            case RequirementType.ReadyForEInvoice: {
                const contactIdentifier = context.contact?.ean
                    ? {
                        scheme: OrganizationIdentifierScheme.GLN,
                        value: context.contact?.ean,
                    }
                    : context.contact?.registryIdentifier
                        ? {
                            scheme: context.contact?.registryIdentifier.scheme,
                            value: context.contact?.registryIdentifier.value,
                        }
                        : null;
                if (!contactIdentifier) {
                    return false;
                }
                if (![
                    // OrganizationIdentifierScheme.DK_CVR,
                    OrganizationIdentifierScheme.GLN,
                    OrganizationIdentifierScheme.NO_ORG,
                    OrganizationIdentifierScheme.NO_ORGNR,
                ].includes(contactIdentifier.scheme)) {
                    return false;
                }
                const deliverability = (context.contact?.deliverability?.eInvoice ?? [])
                    .map((x) => x)
                    .reverse();
                const attempt = deliverability.find((attempt) => attempt.recipient.organizationIdentifier.scheme ===
                    contactIdentifier.scheme &&
                    attempt.recipient.organizationIdentifier.value ===
                        contactIdentifier.value);
                if (attempt == null || attempt.success) {
                    return true;
                }
                return false;
            }
            case RequirementType.DebtRemarks: {
                const debtRemarksCount = ((context.contact && getRKIRegistrations(context.contact)) ||
                    []).length;
                if (requirement.details.max) {
                    return debtRemarksCount <= requirement.details.count;
                }
                else {
                    return debtRemarksCount >= requirement.details.count;
                }
            }
            case RequirementType.ReadyForEboks: {
                const deliverability = (context.contact?.deliverability?.eboks ?? [])
                    .map((x) => x)
                    .reverse();
                if (context.contact == null) {
                    return false;
                }
                const centralRegisterId = context.isCompany
                    ? context.contact.cvr
                    : context.contact.cpr;
                if (centralRegisterId &&
                    (context.isCompany
                        ? validateCVR(context.contact.cvr)
                        : validateCPR(context.contact.cpr))) {
                    const attempt = deliverability.find((attempt) => attempt.recipient.type === EBoksRecipientType.CentralRegisterId);
                    if (attempt == null || attempt.success) {
                        return true;
                    }
                }
                const address = context.contact.address;
                if (context.isCompany) {
                    return false;
                }
                if (!context.contact.name) {
                    return false;
                }
                if (address == null) {
                    return false;
                }
                const name = context.contact.name;
                const attempt = deliverability.find((attempt) => isNameAndAddressAttemptAt(attempt.recipient, name, address));
                if (attempt == null || attempt.success) {
                    return true;
                }
                return false;
            }
            case RequirementType.HasUpdate:
                // Case should have one of the selected updates
                return !!context.updates.find((update) => {
                    const updateFound = requirement.details.updateTypeIds.includes(update.updateTypeId) &&
                        (requirement.details.isActive ? updateIsActive(update) : true);
                    return updateFound;
                });
            case RequirementType.MissingUpdate:
                // Case does not have one of the selected updates
                return !!requirement.details.updateTypeIds.find((updateTypeId) => {
                    const updateFound = context.updates
                        .filter((u) => requirement.details.isActive ? updateIsActive(u) : true)
                        .map((u) => u.updateTypeId)
                        .includes(updateTypeId);
                    return !updateFound;
                });
            case RequirementType.CaseGroup:
                return !!(context.caseGroupId &&
                    requirement.details.caseGroupIds.includes(context.caseGroupId));
            case RequirementType.PercentageSplit: {
                const fraction = uuidToFraction(context.caseId);
                const from = requirement.details.from;
                const to = requirement.details.to;
                return fraction >= from && fraction <= to;
            }
            case RequirementType.AmountThreshold: {
                const balance = requirement.details.onlySelectedCategories?.length
                    ? -requirement.details.onlySelectedCategories.reduce((acc, category) => acc +
                        economyHelpers.overview.balanceCategory(context.caseVouchers, category), 0)
                    : -economyHelpers.overview.balance(context.caseVouchers);
                if (requirement.details.moreThan) {
                    return balance >= requirement.details.amount;
                }
                else {
                    return balance <= requirement.details.amount;
                }
            }
            case RequirementType.HasFoundation: {
                if (requirement.details.onlySelectedFoundationTypes &&
                    context.foundationTypes?.length > 0) {
                    return (context.hasFoundation &&
                        requirement.details.foundationTypes.some((type) => context.foundationTypes.includes(type)));
                }
                else {
                    return context.hasFoundation;
                }
            }
            case RequirementType.DoesNotHaveFoundation:
                return !context.hasFoundation;
            case RequirementType.SpecificCreditor:
                return (requirement.details.creditorIds ?? []).includes(context.creditorId);
            case RequirementType.CustomerHasTag:
                return (context.customerTags || [])
                    .map((tag) => tag.tag)
                    .includes(requirement.details.tag);
            case RequirementType.CaseHasTag: {
                return (context.caseTags || [])
                    .map((tag) => tag.tag)
                    .includes(requirement.details.tag);
            }
            case RequirementType.HasIncomingInteractions:
                return (context.interactions.filter((i) => i.direction === Direction.INCOMING &&
                    ![InteractionType.ClientNote, InteractionType.Note].includes(i.type)).length > 0);
            case RequirementType.CaseStatusIn: {
                const inCaseStatus = requirement.details.caseStatuses.includes(context.caseStatus);
                const onlyEndReasonIds = requirement.details.onlyEndReasonIds
                    ? !!context.endReasonId &&
                        requirement.details.onlyEndReasonIds.includes(context.endReasonId)
                    : true;
                return inCaseStatus && onlyEndReasonIds;
            }
            case RequirementType.Property:
                return propertyRequirementFulfilled(context.properties, requirement.details);
            case RequirementType.InDebtRegister:
                return isInDebtRegister(context.debtRegistrations, requirement.details);
            case RequirementType.NotInDebtRegister:
                return !isInDebtRegister(context.debtRegistrations, requirement.details);
        }
    });
};
const isInDebtRegister = (debtRegistrations, details) => debtRegistrations.some((registration) => registration.state !== DebtRegistrationState.ERROR &&
    details.registries.includes(registration.registry) &&
    (!details.onlyOpenRegistries ||
        registration.registryType === RegistryType.OPEN));
const propertyRequirementFulfilled = (properties, details) => {
    const _properties = properties.filter((p) => p.handle === details.handle &&
        p.type === details.type &&
        p.ref === details.ref);
    // If no properties exist, checkbox false is considered fulfilled
    if (_properties.length === 0 &&
        details.type === PropertyDataType.checkbox &&
        !details.checked) {
        return true;
    }
    return _properties.some((property) => {
        if (details.type === property.type) {
            switch (details.type) {
                case PropertyDataType.singleLineText:
                    return details.in.includes(property.data);
                case PropertyDataType.dropdown:
                    return details.in.includes(property.data);
                case PropertyDataType.checkbox:
                    return property.data == details.checked;
                default:
                    throw new Error('PropertyDataType not handled');
            }
        }
    });
};
const addressValid = (contact) => {
    if (!contact?.address) {
        return false;
    }
    // const letterDeliverability = (contact.deliverability?.letter ?? [])
    //   .map((x) => x)
    //   .reverse()
    // const addresNotDeliverable = letterDeliverability.find(
    //   (d) => compareAddresses(d.address, contact.address) && d.success === false,
    // )
    // if (addresNotDeliverable) {
    //   return false
    // }
    return validateAddress(contact.address);
};
const updateIsActive = (update) => {
    if (moment(update.pausedAt).isBefore(moment())) {
        if (update.continuedAt) {
            return moment().isBefore(moment(update.continuedAt));
        }
        else {
            return true;
        }
    }
    else {
        return false;
    }
};
export var NextTaskType;
(function (NextTaskType) {
    NextTaskType["NoNextTask"] = "NO_NEXT_TASK";
    NextTaskType["ManualTask"] = "MANUAL_TASK";
    NextTaskType["NormalTask"] = "NORMAL_TASK";
    NextTaskType["StartingPointTask"] = "STARTING_POINT_TASK";
})(NextTaskType || (NextTaskType = {}));
export const isPossiblePath = (path, context, onlyPathTypes) => (!onlyPathTypes || onlyPathTypes.includes(path.type)) &&
    fulfilRequirements(path.requirements, context);
export const isPossibleTask = (task, context, paths, onlyPathTypes) => {
    if (task.startingPoint) {
        return fulfilRequirements(task.startingPointRequirements || [], context);
    }
    else {
        const availablePaths = (paths || []).filter((path) => isPossiblePath(path, context, onlyPathTypes));
        return availablePaths.length > 0;
    }
};
export const getPastUpdate = (updates, workflowId) => {
    const relevantUpdates = updates
        .filter((update) => !!update.taskId)
        .filter((update) => update.state !== UpdateState.RolledBack);
    const updatesToConsider = [];
    const updatesOnSameWorkflow = relevantUpdates.filter((update) => update.byWorkflowId === workflowId);
    updatesToConsider.push(...updatesOnSameWorkflow);
    const hasExecutedOnWorkflowBefore = relevantUpdates.some((update) => !!update.byWorkflowId);
    if (!hasExecutedOnWorkflowBefore) {
        // For migrated cases we need to use the migration note
        // to place the workflow in the correct initial workflow step
        // using alternative paths
        const updatesOnOtherWorkflows = relevantUpdates.filter((update) => !update.byWorkflowId);
        updatesToConsider.push(...updatesOnOtherWorkflows);
    }
    return updatesToConsider.sort((a, b) => moment(b.pausedAt).toDate().getTime() -
        moment(a.pausedAt).toDate().getTime())[0];
};
const debtRegisterRequirementTypes = [
    RequirementType.InDebtRegister,
    RequirementType.NotInDebtRegister,
];
export const hasDebtRegisterRequirement = (requirements) => requirements.some((requirement) => debtRegisterRequirementTypes.includes(requirement.type));
export const usesDebtRegister = (workflow) => hasDebtRegisterRequirement([
    ...workflow.tasks.flatMap((task) => task.startingPointRequirements ?? []),
    ...workflow.paths.flatMap((path) => path.requirements),
]);
export const getNextRequirements = (workflow, updates) => {
    const pastUpdate = getPastUpdate(updates, workflow.workflowId);
    let requirements = [];
    if (!pastUpdate) {
        // If this task is the first one
        const startingPointTasks = workflow.tasks.filter((task) => task.startingPoint);
        requirements = requirements.concat(startingPointTasks
            .map((t) => t.startingPointRequirements || [])
            .reduce((acc, val) => acc.concat(val), []));
    }
    else {
        // If this task is not the first one
        const nextPossiblePaths = getNextTasks(workflow, pastUpdate.taskId);
        requirements = requirements.concat(nextPossiblePaths
            .map(({ path }) => path.requirements)
            .reduce((acc, val) => acc.concat(val), []));
    }
    return requirements;
};
export const getNextTask = (workflow, context) => {
    const pastUpdate = getPastUpdate(context.updates, workflow.workflowId);
    if (!pastUpdate) {
        // If this task is the first one
        const startingPointTasks = workflow.tasks
            .filter((task) => task.startingPoint && isPossibleTask(task, context, workflow.paths))
            .sort((a, b) => (a.startingPoint ? a.startingPointPriority || 0 : 0) -
            (b.startingPoint ? b.startingPointPriority || 0 : 0));
        const startingPointTask = startingPointTasks[0];
        if (!startingPointTask) {
            return { type: NextTaskType.NoNextTask, noNextStep: true, pastUpdate };
        }
        return {
            type: NextTaskType.StartingPointTask,
            pastUpdate,
            task: startingPointTask,
            path: null,
            date: moment()
                .tz('Europe/Copenhagen')
                .set('hour', 7)
                .startOf('hour')
                .format(),
            tasks: startingPointTasks,
            manual: (startingPointTask.startingPoint &&
                startingPointTask.startingPointManual) ||
                undefined,
        };
    }
    else {
        // If this task is not the first one
        const nextPossiblePaths = getNextTasks(workflow, pastUpdate.taskId);
        const nextPossibleTaskPaths = nextPossiblePaths
            .filter(({ path }) => isPossiblePath(path, context))
            .map(({ path, task }) => {
            return { task, path, pastUpdate };
        })
            .sort((a, b) => a.path.priority - b.path.priority);
        const nextPossibleTaskPath = nextPossibleTaskPaths[0];
        if (!nextPossibleTaskPath) {
            return {
                type: NextTaskType.NoNextTask,
                noNextStep: true,
                pastUpdate,
            };
        }
        const { task, path } = nextPossibleTaskPath;
        // Check if task is manual
        if (path.manual) {
            return {
                type: NextTaskType.ManualTask,
                task,
                path,
                manual: true,
                tasks: nextPossibleTaskPaths.map(({ task }) => task),
                pastUpdate,
            };
        }
        else {
            return {
                type: NextTaskType.NormalTask,
                task,
                path,
                date: addTiming(pastUpdate.pausedAt, path.timing, context.caseVouchers),
                pastUpdate,
            };
        }
    }
};
export const getNextTasks = (workflow, fromTaskId) => {
    if (!fromTaskId) {
        return [];
    }
    const paths = workflow.paths.filter((path) => path.source === fromTaskId);
    return paths.map((path) => {
        const task = workflow.tasks.find((task) => task.id === path.target);
        if (!task) {
            throw new Error('Workflow corrupt. Could not find taskId: ' + path.target);
        }
        return {
            path,
            task,
        };
    });
};
export const getProcedureTask = (updates, tasks, context) => {
    // If this task is the first one
    const startingPointTasks = tasks
        .filter((task) => isPossibleTask(task, context, []))
        .sort((a, b) => (a.startingPoint ? a.startingPointPriority || 0 : 0) -
        (b.startingPoint ? b.startingPointPriority || 0 : 0));
    const startingPointTask = startingPointTasks[0];
    if (!startingPointTask) {
        return null;
    }
    return {
        task: startingPointTask,
        manual: (startingPointTask.startingPoint &&
            startingPointTask.startingPointManual) ||
            undefined,
    };
};
export const CaseGroupWorkflowsSchema = z.object({
    workflowId: z.string(),
    caseGroupId: z.string(),
    isDunning: z.boolean(),
});
export var CaseGroupWorkflowsLevels;
(function (CaseGroupWorkflowsLevels) {
    CaseGroupWorkflowsLevels["Tenant"] = "tenant";
    CaseGroupWorkflowsLevels["Creditor"] = "creditor";
})(CaseGroupWorkflowsLevels || (CaseGroupWorkflowsLevels = {}));
export const getWorkflowForCase = (creditor, tenant, caseGroupId) => {
    let setOnTenant = !creditor.defaultWorkflowId && tenant.defaultWorkflowId != null;
    let setOnCreditor = !!creditor.defaultWorkflowId;
    let caseGroupWorkflow;
    if (creditor.caseGroupWorkflows.length > 0 && caseGroupId) {
        caseGroupWorkflow = creditor.caseGroupWorkflows.find((cg) => cg.caseGroupId === caseGroupId)?.workflowId;
        if (caseGroupWorkflow) {
            setOnTenant = false;
            setOnCreditor = true;
        }
    }
    if (tenant.caseGroupWorkflows.length > 0 &&
        !caseGroupWorkflow &&
        caseGroupId) {
        caseGroupWorkflow = tenant.caseGroupWorkflows.find((cg) => cg.caseGroupId === caseGroupId)?.workflowId;
        if (caseGroupWorkflow) {
            setOnTenant = true;
            setOnCreditor = false;
        }
    }
    return {
        workflowId: caseGroupWorkflow
            ? caseGroupWorkflow
            : creditor.defaultWorkflowId || tenant.defaultWorkflowId,
        setOnTenant,
        setOnCreditor,
    };
};
export const getStages = (tasks) => {
    const stagesMap = tasks.reduce((acc, task) => {
        const stageId = task.workflowStageId;
        if (stageId == null) {
            return acc;
        }
        if (!acc[stageId]) {
            acc[stageId] = {
                stageId,
                positions: [],
                taskIdMap: {},
            };
        }
        acc[stageId] = {
            stageId,
            positions: acc[stageId].positions.concat(task.position),
            taskIdMap: {
                ...acc[stageId].taskIdMap,
                [task.id]: true,
            },
        };
        return acc;
    }, {});
    const stagesOrdered = Object.values(stagesMap)
        .map((stage) => {
        return {
            ...stage,
            minDistance: stage.positions.reduce((acc, pos) => {
                const _distance = distance(pos);
                if (_distance < acc) {
                    return _distance;
                }
                else {
                    return acc;
                }
            }, Infinity),
        };
    })
        .sort((a, b) => a.minDistance - b.minDistance);
    return stagesOrdered.map((s) => ({
        stageId: s.stageId,
        taskIdMap: s.taskIdMap,
    }));
};
const distance = (position) => {
    const a = 500 - position.x;
    const b = 120 - position.y;
    return Math.sqrt(a * a + b * b);
};
export var PathWarning;
(function (PathWarning) {
    PathWarning["ValidEmailRequirementRecommended"] = "VALID_EMAIL_REQUIREMENT_RECOMMENDED";
    PathWarning["ValidAddressRequirementRecommended"] = "VALID_ADDRESS_REQUIREMENT_RECOMMENDED";
    PathWarning["ReadyForEboksRequirementRecommended"] = "READY_FOR_EBOKS_REQUIREMENT_RECOMMENDED";
})(PathWarning || (PathWarning = {}));
//# sourceMappingURL=index.js.map